import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Briefcase, Plus, Loader2, Users, Shield, Eye, EyeOff, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OpportunitiesAdminPage() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editOpp, setEditOpp] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/api/opportunities/admin/list`, { headers: { Authorization: `Bearer ${token}` } });
      setOpps(res.data);
    } catch { toast.error('Erro ao carregar vagas'); }
    finally { setLoading(false); }
  };

  const toggleActive = async (opp) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/api/opportunities/admin/${opp.id}`, { active: !opp.active }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(opp.active ? 'Vaga desativada' : 'Vaga reativada');
      load();
    } catch { toast.error('Erro'); }
  };

  return (
    <div data-testid="admin-opportunities-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Briefcase className="h-6 w-6 text-emerald-500" />Mural de Vagas</h1>
          <p className="text-sm text-zinc-400 mt-1">Gerenciar oportunidades de reintegração</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="create-opportunity-btn">
          <Plus className="h-4 w-4" />Nova Vaga
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>
      ) : opps.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-8 text-center text-zinc-500">Nenhuma vaga cadastrada.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {opps.map(opp => (
            <Card key={opp.id} className="bg-zinc-900 border-zinc-800" data-testid={`opp-card-${opp.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-white text-sm truncate">{opp.title}</h3>
                    {opp.type === 'priority' && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]"><Shield className="h-2.5 w-2.5 mr-0.5" />Prioritária</Badge>}
                    {!opp.active && <Badge className="bg-red-500/20 text-red-400 text-[10px]">Inativa</Badge>}
                  </div>
                  <p className="text-xs text-zinc-400">{opp.company} · {opp.location || 'Sem local'}</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <Users className="h-3.5 w-3.5" />{opp.applications_count}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditOpp(opp)} className="text-zinc-400 hover:text-emerald-400" data-testid={`edit-opp-btn-${opp.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => toggleActive(opp)} className="text-zinc-400 hover:text-white" data-testid={`toggle-opp-btn-${opp.id}`}>
                  {opp.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateOppModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      <EditOppModal opp={editOpp} onClose={() => setEditOpp(null)} onSaved={() => { setEditOpp(null); load(); }} />
    </div>
  );
}

function CreateOppModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('general');
  const [location, setLocation] = useState('');
  const [applyLink, setApplyLink] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!title.trim() || !company.trim()) { toast.error('Título e empresa são obrigatórios'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/api/opportunities/admin/create`, {
        title, company, description, type, location, apply_link: applyLink,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Vaga criada!');
      setTitle(''); setCompany(''); setDescription(''); setType('general'); setLocation(''); setApplyLink('');
      onCreated();
    } catch { toast.error('Erro ao criar vaga'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">Nova Vaga</DialogTitle>
          <DialogDescription className="text-zinc-400">Cadastrar oportunidade no Mural</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-zinc-300">Título *</Label><Input data-testid="create-opp-title" value={title} onChange={e => setTitle(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div><Label className="text-zinc-300">Empresa *</Label><Input data-testid="create-opp-company" value={company} onChange={e => setCompany(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div><Label className="text-zinc-300">Descrição</Label><Textarea data-testid="create-opp-description" value={description} onChange={e => setDescription(e.target.value)} rows={3} className="mt-1 bg-zinc-800 border-zinc-700 resize-none" /></div>
          <div><Label className="text-zinc-300">Local</Label><Input data-testid="create-opp-location" value={location} onChange={e => setLocation(e.target.value)} placeholder="Ex: São Paulo, SP ou Remoto" className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div><Label className="text-zinc-300">Link de candidatura</Label><Input data-testid="create-opp-link" value={applyLink} onChange={e => setApplyLink(e.target.value)} placeholder="https://..." className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div>
            <Label className="text-zinc-300">Tipo</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="create-opp-type" className="mt-1 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">Geral</SelectItem>
                <SelectItem value="priority">Prioritária (requer Selo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="create-opp-submit" onClick={handleCreate} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Criar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditOppModal({ opp, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('general');
  const [location, setLocation] = useState('');
  const [applyLink, setApplyLink] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opp) {
      setTitle(opp.title || '');
      setCompany(opp.company || '');
      setDescription(opp.description || '');
      setType(opp.type || 'general');
      setLocation(opp.location || '');
      setApplyLink(opp.apply_link || '');
      setActive(opp.active !== false);
    }
  }, [opp]);

  const handleSave = async () => {
    if (!title.trim() || !company.trim()) { toast.error('Título e empresa são obrigatórios'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/api/opportunities/admin/${opp.id}`, {
        title, company, description, type, apply_link: applyLink, location, active,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Vaga atualizada!');
      onSaved();
    } catch { toast.error('Erro ao atualizar vaga'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!opp} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-700" data-testid="edit-opp-modal">
        <DialogHeader>
          <DialogTitle className="text-white">Editar Vaga</DialogTitle>
          <DialogDescription className="text-zinc-400">Alterar dados da oportunidade</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
          <div><Label className="text-zinc-300">Título *</Label><Input data-testid="edit-opp-title" value={title} onChange={e => setTitle(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div><Label className="text-zinc-300">Empresa *</Label><Input data-testid="edit-opp-company" value={company} onChange={e => setCompany(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div><Label className="text-zinc-300">Descrição</Label><Textarea data-testid="edit-opp-description" value={description} onChange={e => setDescription(e.target.value)} rows={3} className="mt-1 bg-zinc-800 border-zinc-700 resize-none" /></div>
          <div><Label className="text-zinc-300">Local</Label><Input data-testid="edit-opp-location" value={location} onChange={e => setLocation(e.target.value)} placeholder="Ex: São Paulo, SP ou Remoto" className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div><Label className="text-zinc-300">Link de candidatura</Label><Input data-testid="edit-opp-link" value={applyLink} onChange={e => setApplyLink(e.target.value)} placeholder="https://..." className="mt-1 bg-zinc-800 border-zinc-700" /></div>
          <div>
            <Label className="text-zinc-300">Tipo</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="edit-opp-type" className="mt-1 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">Geral</SelectItem>
                <SelectItem value="priority">Prioritária (requer Selo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300">Status</Label>
            <Select value={active ? 'active' : 'inactive'} onValueChange={v => setActive(v === 'active')}>
              <SelectTrigger data-testid="edit-opp-status" className="mt-1 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativa</SelectItem>
                <SelectItem value="inactive">Inativa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="edit-opp-submit" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
